import { proxyUrl, type MediaRef } from "./api.ts";
import { el, formatClock, showAlert, showToast } from "./dom.ts";
import { icons } from "./icons.ts";
import {
  getState,
  recordProgress,
  toPlaybackRecord,
  updateSettings,
  videoKey,
  type Quality,
} from "./store.ts";

export interface PlayerQueue {
  position: number;
  length: number;
  onNext: () => void;
  onPrevious: () => void;
}

export interface PlayerTarget {
  media: MediaRef[];
  title: string;
  subtitle?: string;
  backdrop?: string;
  provider: string;
  videoId: string;
  startAt?: number;
  queue?: PlayerQueue;
  onEnded?: () => void;
}

interface HlsInstance {
  destroy(): void;
  startLoad(position?: number): void;
  loadSource(url: string): void;
  attachMedia(media: HTMLVideoElement): void;
  on?(event: string, handler: (event: string, data: { fatal?: boolean }) => void): void;
}

interface HlsConstructor {
  new (config?: Record<string, unknown>): HlsInstance;
  isSupported?: () => boolean;
}

declare global {
  interface Window {
    Hls?: HlsConstructor;
  }
}

type PlayerState =
  | { status: "idle" }
  | { status: "loading"; candidate: number }
  | { status: "playing"; candidate: number }
  | { status: "paused"; candidate: number }
  | { status: "buffering"; candidate: number }
  | { status: "error"; candidate: number; retryable: boolean }
  | { status: "ended" };

interface ChromeRefs {
  root: HTMLElement;
  video: HTMLVideoElement;
  close: HTMLButtonElement;
  top: HTMLElement;
  controls: HTMLElement;
  seek: HTMLElement;
  fill: HTMLElement;
  buffer: HTMLElement;
  thumb: HTMLElement;
  bubble: HTMLElement;
  elapsed: HTMLElement;
  remaining: HTMLElement;
  previous: HTMLButtonElement;
  back10: HTMLButtonElement;
  play: HTMLButtonElement;
  forward10: HTMLButtonElement;
  next: HTMLButtonElement;
  mute: HTMLButtonElement;
  volume: HTMLInputElement;
  speedTrigger: HTMLButtonElement;
  speedPanel: HTMLElement;
  qualityTrigger: HTMLButtonElement;
  qualityPanel: HTMLElement;
  captions: HTMLButtonElement;
  pip: HTMLButtonElement;
  fullscreen: HTMLButtonElement;
  autoplay: HTMLButtonElement;
  notice: HTMLElement;
  hud: HTMLElement;
  hudIcon: HTMLElement;
  hudText: HTMLElement;
  error: HTMLElement;
  errorTitle: HTMLElement;
  errorDetail: HTMLElement;
  retry: HTMLButtonElement;
  errorClose: HTMLButtonElement;
  live: HTMLElement;
  rotate: HTMLElement;
}

const RATES = [0.5, 0.75, 1, 1.25, 1.5, 2] as const;

let overlay: HTMLElement | null = null;
let refs: ChromeRefs | null = null;
let video: HTMLVideoElement | null = null;
let hlsInstance: HlsInstance | null = null;
let currentState: PlayerState = { status: "idle" };
let currentTarget: PlayerTarget | null = null;
let restoreFocus: HTMLElement | null = null;

let hideTimer = 0;
let hudTimer = 0;
let tapTimer = 0;
let mouseTapTimer = 0;
let announceTimer = 0;

let generation = 0;
let candidate = 0;
let autoplayEnabled = true;
let pendingResume = 0;
let candidateResume = 0;
let brightness = 1;
let lastRecordAt = 0;

let activeMenu: { trigger: HTMLButtonElement; panel: HTMLElement } | null = null;
let keyHandler: ((event: KeyboardEvent) => void) | null = null;
let fullscreenHandler: (() => void) | null = null;
let documentPointerHandler: ((event: PointerEvent) => void) | null = null;

/**
 * The HLS engine ships with the app as a lazy chunk. Loading it from a CDN at
 * play time meant an ad blocker, an offline moment or a slow third party turned
 * every HLS title into "the stream engine could not be loaded".
 */
async function loadHlsLibrary(): Promise<HlsConstructor | null> {
  if (window.Hls) return window.Hls;
  try {
    const loaded = (await import("hls.js")) as unknown as {
      default?: HlsConstructor;
    } & HlsConstructor;
    const Hls = loaded.default ?? loaded;
    window.Hls = Hls;
    return Hls;
  } catch {
    return null;
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function spokenTime(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  return `${minutes} minute${minutes === 1 ? "" : "s"} ${rest} second${rest === 1 ? "" : "s"}`;
}

function filled(markup: string): string {
  return markup.replace('class="icon"', 'class="icon icon--fill"');
}

function announce(text: string): void {
  if (!refs) return;
  window.clearTimeout(announceTimer);
  refs.live.textContent = "";
  announceTimer = window.setTimeout(() => {
    if (refs) refs.live.textContent = text;
  }, 40);
}

function clearPlayerAlert(): void {
  for (const alert of document.querySelectorAll("#alerts .toast--alert")) {
    if (alert.textContent?.startsWith("Playback failed:")) alert.remove();
  }
}

function showHud(text: string, icon?: string): void {
  if (!refs) return;
  refs.hudIcon.innerHTML = icon ?? "";
  refs.hudText.textContent = text;
  refs.hud.dataset.open = "true";
  window.clearTimeout(hudTimer);
  hudTimer = window.setTimeout(() => {
    if (refs) refs.hud.dataset.open = "false";
  }, 700);
}

function setChrome(visible: boolean): void {
  if (overlay) overlay.dataset.chrome = String(visible);
}

function scheduleHide(): void {
  window.clearTimeout(hideTimer);
  if (!overlay || activeMenu) return;
  if (currentState.status !== "playing" && currentState.status !== "buffering") return;
  hideTimer = window.setTimeout(() => {
    if (!overlay || activeMenu) return;
    if (overlay.contains(document.activeElement)) return;
    if (currentState.status !== "playing" && currentState.status !== "buffering") return;
    setChrome(false);
  }, 3000);
}

function showChrome(): void {
  setChrome(true);
  scheduleHide();
}

function toggleChrome(): void {
  if (!overlay) return;
  if (overlay.dataset.chrome === "false") showChrome();
  else {
    window.clearTimeout(hideTimer);
    setChrome(false);
  }
}

function paintPlay(): void {
  if (!refs) return;
  const playing = currentState.status === "playing" || currentState.status === "buffering";
  refs.play.innerHTML = playing ? filled(icons.pause) : filled(icons.play);
  refs.play.setAttribute("aria-label", playing ? "Pause" : "Play");
  refs.play.setAttribute("aria-pressed", String(playing));
}

function paintVolume(): void {
  if (!refs || !video) return;
  const silent = video.muted || video.volume === 0;
  refs.mute.innerHTML = silent ? icons.mute : icons.volume;
  refs.mute.setAttribute("aria-label", silent ? "Unmute" : "Mute");
  refs.mute.setAttribute("aria-pressed", String(video.muted));
  refs.volume.value = String(video.volume);
  refs.volume.setAttribute("aria-valuetext", `${Math.round(video.volume * 100)} percent`);
}

function paintFullscreen(active: boolean): void {
  if (!refs) return;
  refs.fullscreen.innerHTML = active ? icons.fullscreenExit : icons.fullscreen;
  refs.fullscreen.setAttribute("aria-label", active ? "Exit fullscreen" : "Fullscreen");
  refs.fullscreen.setAttribute("aria-pressed", String(active));
}

function paintPip(active: boolean): void {
  if (!refs) return;
  refs.pip.setAttribute("aria-pressed", String(active));
  refs.pip.setAttribute("aria-label", active ? "Exit picture in picture" : "Picture in picture");
}

function paintCaptions(): void {
  if (!refs || !video) return;
  const tracks = [...video.textTracks];
  refs.captions.disabled = tracks.length === 0;
  const active = tracks.some((track) => track.mode === "showing");
  refs.captions.setAttribute("aria-pressed", String(active));
  refs.captions.setAttribute("aria-label", active ? "Hide captions" : "Show captions");
}

function paintAutoplay(): void {
  if (!refs) return;
  refs.autoplay.setAttribute("aria-pressed", String(autoplayEnabled));
  refs.autoplay.dataset.state = autoplayEnabled ? "on" : "off";
}

function persistProgress(force = false): void {
  if (!video || !currentTarget || currentState.status === "error") return;
  const now = Date.now();
  if (!force && now - lastRecordAt < 4000) return;
  lastRecordAt = now;
  recordProgress(
    toPlaybackRecord(
      {
        id: currentTarget.videoId,
        title: currentTarget.title,
        thumbnail: currentTarget.backdrop,
        provider: currentTarget.provider,
      },
      video.currentTime,
      video.duration,
    ),
  );
}

function paintProgress(): void {
  if (!refs || !video) return;
  const duration = Number.isFinite(video.duration) ? video.duration : 0;
  const now = Number.isFinite(video.currentTime) ? video.currentTime : 0;
  const ratio = duration > 0 ? clamp(now / duration, 0, 1) : 0;
  refs.fill.style.transform = `scaleX(${ratio})`;
  const trackWidth = refs.seek.clientWidth;
  const thumbWidth = refs.thumb.offsetWidth || 12;
  refs.thumb.style.transform = `translateX(${Math.max(0, ratio * Math.max(0, trackWidth - thumbWidth))}px)`;
  let buffered = 0;
  try {
    if (video.buffered.length > 0) buffered = video.buffered.end(video.buffered.length - 1);
  } catch {
    buffered = 0;
  }
  refs.buffer.style.transform = `scaleX(${duration > 0 ? clamp(buffered / duration, 0, 1) : 0})`;
  refs.elapsed.textContent = formatClock(now);
  refs.remaining.textContent = `-${formatClock(Math.max(0, duration - now))}`;
  refs.seek.setAttribute("aria-valuemax", String(Math.round(duration)));
  refs.seek.setAttribute("aria-valuenow", String(Math.round(now)));
  refs.seek.setAttribute(
    "aria-valuetext",
    duration > 0 ? `${spokenTime(now)} of ${spokenTime(duration)}` : spokenTime(now),
  );
  persistProgress();
}

function setPlayerState(next: PlayerState): void {
  const previous = currentState;
  currentState = next;
  if (overlay) {
    overlay.dataset.status = next.status;
    overlay.dataset.error = String(next.status === "error");
  }
  if (refs) {
    refs.error.hidden = next.status !== "error";
    const notice =
      next.status === "loading" ? "Loading stream" : next.status === "buffering" ? "Buffering" : "";
    refs.notice.textContent = notice;
    refs.notice.hidden = notice === "";
  }
  paintPlay();
  if (next.status === "error") {
    showChrome();
    announce("Playback failed");
  } else if (previous.status !== next.status) {
    if (previous.status === "error") clearPlayerAlert();
    const labels: Record<PlayerState["status"], string> = {
      idle: "",
      loading: "Loading stream",
      playing: "Playing",
      paused: "Paused",
      buffering: "Buffering",
      error: "Playback failed",
      ended: "Playback ended",
    };
    announce(labels[next.status]);
  }
  if (next.status === "playing" || next.status === "buffering") scheduleHide();
  else {
    window.clearTimeout(hideTimer);
    setChrome(true);
  }
}

function failCandidate(index: number, message: string): void {
  if (index + 1 < ordered.length) {
    announce("That stream failed. Trying the next quality.");
    void attach(index + 1);
    return;
  }
  if (refs) refs.errorDetail.textContent = message;
  showAlert(`Playback failed: ${message}`);
  setPlayerState({ status: "error", candidate: index, retryable: false });
}

let ordered: MediaRef[] = [];

async function attach(index: number, resumeAt = 0): Promise<void> {
  const player = video;
  const ui = refs;
  if (!player || !ui) return;
  const ref = ordered[index];
  if (!ref) {
    failCandidate(index, "No playable stream was found for this title.");
    return;
  }
  const token = ++generation;
  candidate = index;
  candidateResume = resumeAt;
  ui.error.hidden = true;
  setPlayerState({ status: "loading", candidate: index });
  paintQuality();
  if (hlsInstance) {
    hlsInstance.destroy();
    hlsInstance = null;
  }
  const source = withQuality(proxyUrl(ref), ref);
  // Ask the engine whether it can play HLS, not the browser: every Chromium
  // build answers `canPlayType` for a playlist it cannot actually play, and
  // the media element then fails with no useful error.
  const engine = ref.type === "hls" ? await loadHlsLibrary() : null;
  const enginePlays = engine ? (engine.isSupported?.() ?? true) : false;
  const nativePlays = player.canPlayType("application/vnd.apple.mpegurl") === "probably";
  if (ref.type === "hls" && enginePlays) {
    const Hls = engine;
    if (!overlay || token !== generation) return;
    if (!Hls) {
      failCandidate(index, "The stream engine could not be loaded. Check the connection and retry.");
      return;
    }
    const instance = new Hls({
      enableWorker: true,
      // These are VOD files, not low-latency live edges. Low-latency mode makes
      // hls.js chase a live position, which is exactly what breaks seeking.
      lowLatencyMode: false,
      maxBufferLength: 30,
      backBufferLength: 30,
      maxBufferSize: 60 * 1000 * 1000,
    });
    hlsInstance = instance;
    instance.on?.("hlsError", (_event, data) => {
      if (token !== generation || !data.fatal) return;
      const details = String((data as { details?: string }).details ?? "");
      // A seek that lands between buffered ranges, or a slow segment, is
      // recoverable: hls.js can reload around the new position. Killing the
      // stream here is what made seeking look like a failure.
      if (/BufferStalled|BufferSeekOverHole|BufferAppend|BufferNudge/i.test(details)) {
        try {
          instance.startLoad(player.currentTime);
        } catch {
          /* the next retry covers it */
        }
        return;
      }
      failCandidate(index, "The stream host refused this source. Try another quality.");
    });
    instance.loadSource(source);
    instance.attachMedia(player);
  } else if (ref.type === "hls" && !nativePlays) {
    failCandidate(index, "This browser cannot play this stream. Try another quality.");
    return;
  } else {
    player.src = source;
  }
  try {
    await player.play();
  } catch {
    if (token === generation && currentState.status === "loading") {
      setPlayerState({ status: "paused", candidate: index });
      announce("Press play to start");
    }
  }
}

function mediaErrorMessage(error: MediaError | null): string {
  switch (error?.code) {
    case 1:
      return "Playback was interrupted before the stream started. Retry the source.";
    case 2:
      return "The stream host could not be reached. Try another quality or retry.";
    case 3:
      return "The stream could not be decoded. Try another quality.";
    case 4:
      return "This stream format is not supported by the browser. Try another quality.";
    default:
      return "The source host refused the request. Try another quality or retry.";
  }
}

function seekBy(delta: number): void {
  if (!video) return;
  const duration = Number.isFinite(video.duration) ? video.duration : 0;
  video.currentTime = clamp(video.currentTime + delta, 0, duration || video.currentTime + delta);
  paintProgress();
  announce(delta > 0 ? `Forward ${Math.abs(delta)} seconds` : `Back ${Math.abs(delta)} seconds`);
}

function seekToRatio(ratio: number): void {
  if (!video) return;
  const duration = Number.isFinite(video.duration) ? video.duration : 0;
  video.currentTime = clamp(ratio, 0, 1) * duration;
  paintProgress();
}

async function togglePlay(): Promise<void> {
  const player = video;
  if (!player) return;
  if (player.paused) {
    if (player.ended) player.currentTime = 0;
    try {
      await player.play();
    } catch {
      showToast("Playback was blocked by the browser");
      announce("Playback was blocked by the browser");
      if (currentState.status !== "error" && currentState.status !== "ended") {
        setPlayerState({ status: "paused", candidate });
      }
    }
  } else {
    player.pause();
  }
}

function applyVolume(value: number, withHud = true): void {
  const player = video;
  if (!player) return;
  const next = clamp(value, 0, 1);
  player.volume = next;
  if (next > 0 && player.muted) {
    player.muted = false;
    updateSettings({ muted: false });
  }
  paintVolume();
  if (withHud) {
    const silent = player.muted || next === 0;
    showHud(`Volume ${Math.round(next * 100)}%`, silent ? icons.mute : icons.volume);
  }
}

function toggleMute(): void {
  const player = video;
  if (!player) return;
  player.muted = !player.muted;
  if (!player.muted && player.volume === 0) player.volume = 0.5;
  updateSettings({ muted: player.muted });
  paintVolume();
  showHud(player.muted ? "Muted" : "Sound on", player.muted ? icons.mute : icons.volume);
  announce(player.muted ? "Muted" : "Sound on");
}

function applyBrightness(value: number, withHud = true): void {
  brightness = clamp(value, 0.25, 1.25);
  if (video) video.style.filter = brightness === 1 ? "" : `brightness(${brightness})`;
  if (withHud) {
    showHud(`Brightness ${Math.round(brightness * 100)}%`);
    announce(`Brightness ${Math.round(brightness * 100)} percent`);
  }
}

async function toggleFullscreen(): Promise<void> {
  const root = overlay;
  const player = video;
  if (!root || !player) return;
  const doc = document as Document & {
    webkitFullscreenElement?: Element | null;
    webkitExitFullscreen?: () => Promise<void> | void;
  };
  const safariVideo = player as HTMLVideoElement & { webkitEnterFullscreen?: () => void };
  try {
    if (document.fullscreenElement || doc.webkitFullscreenElement) {
      if (document.exitFullscreen) await document.exitFullscreen();
      else await doc.webkitExitFullscreen?.();
    } else if (root.requestFullscreen) {
      await root.requestFullscreen();
    } else if (safariVideo.webkitEnterFullscreen) {
      safariVideo.webkitEnterFullscreen();
    } else {
      throw new Error("unsupported");
    }
  } catch {
    showToast("Fullscreen is unavailable in this browser");
    announce("Fullscreen is unavailable in this browser");
  }
}

async function togglePip(): Promise<void> {
  const player = video;
  if (!player) return;
  const ios = player as HTMLVideoElement & {
    webkitSetPresentationMode?: (mode: string) => void;
    webkitPresentationMode?: string;
  };
  try {
    if (document.pictureInPictureElement) {
      await document.exitPictureInPicture();
    } else if (player.requestPictureInPicture) {
      await player.requestPictureInPicture();
    } else if (ios.webkitSetPresentationMode) {
      ios.webkitSetPresentationMode(ios.webkitPresentationMode === "picture-in-picture" ? "inline" : "picture-in-picture");
    } else {
      throw new Error("unsupported");
    }
  } catch {
    showToast("Picture-in-picture is unavailable in this browser");
    announce("Picture-in-picture is unavailable in this browser");
  }
}

function toggleCaptions(): void {
  const player = video;
  if (!player) return;
  const tracks = [...player.textTracks];
  if (tracks.length === 0) return;
  const showing = tracks.some((track) => track.mode === "showing");
  for (const track of tracks) track.mode = showing ? "disabled" : "showing";
  paintCaptions();
  announce(showing ? "Captions off" : "Captions on");
}

function qualityLabel(ref: MediaRef): string {
  if (ref.quality) return `${ref.quality} ${ref.type.toUpperCase()}`;
  return ref.type.toUpperCase();
}

let paintQuality = (): void => undefined;

function makeMenu(
  className: string,
  label: string,
  triggerText: string,
): { wrap: HTMLElement; trigger: HTMLButtonElement; panel: HTMLElement } {
  const trigger = el("button", {
    class: `player-btn player-btn--menu ${className}`,
    attrs: {
      type: "button",
      "aria-haspopup": "menu",
      "aria-expanded": "false",
      "aria-label": label,
    },
    text: triggerText,
  });
  const panel = el("div", {
    class: "player-menu",
    attrs: { role: "menu", "aria-label": label },
  });
  panel.hidden = true;
  const wrap = el("div", { class: "player-menu-wrap" }, trigger, panel);
  return { wrap, trigger, panel };
}

function openMenu(trigger: HTMLButtonElement, panel: HTMLElement): void {
  if (activeMenu) closeMenu(false);
  activeMenu = { trigger, panel };
  panel.hidden = false;
  trigger.setAttribute("aria-expanded", "true");
  window.clearTimeout(hideTimer);
  const items = [...panel.querySelectorAll<HTMLButtonElement>("button:not([disabled])")];
  const checked = items.find((item) => item.getAttribute("aria-checked") === "true") ?? items[0];
  checked?.focus({ preventScroll: true });
}

function closeMenu(restoreFocusToTrigger: boolean): void {
  if (!activeMenu) return;
  const { trigger, panel } = activeMenu;
  activeMenu = null;
  panel.hidden = true;
  trigger.setAttribute("aria-expanded", "false");
  if (restoreFocusToTrigger) trigger.focus({ preventScroll: true });
  scheduleHide();
}

function wireMenu(trigger: HTMLButtonElement, panel: HTMLElement): void {
  trigger.addEventListener("click", (event) => {
    event.stopPropagation();
    if (activeMenu?.panel === panel) closeMenu(true);
    else openMenu(trigger, panel);
  });
  trigger.addEventListener("keydown", (event) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      openMenu(trigger, panel);
    }
  });
  panel.addEventListener("keydown", (event) => {
    const items = [...panel.querySelectorAll<HTMLButtonElement>("button:not([disabled])")];
    const index = items.indexOf(document.activeElement as HTMLButtonElement);
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      items[(index + step + items.length) % items.length]?.focus();
    } else if (event.key === "Home" || event.key === "End") {
      event.preventDefault();
      (event.key === "Home" ? items[0] : items[items.length - 1])?.focus();
    }
  });
}

function menuItem(
  panel: HTMLElement,
  label: string,
  checked: boolean,
  onSelect: () => void,
): HTMLButtonElement {
  const item = el("button", {
    class: "player-menu-item",
    attrs: {
      type: "button",
      role: "menuitemradio",
      "aria-checked": String(checked),
    },
    text: label,
  });
  item.addEventListener("click", () => {
    onSelect();
    closeMenu(true);
  });
  panel.append(item);
  return item;
}

function buildOverlay(target: PlayerTarget): { root: HTMLElement; refs: ChromeRefs } {
  const player = el("video", {
    class: "player-video",
    attrs: { playsinline: "", preload: "metadata" },
  });

  const close = el("button", {
    class: "player-btn player-btn--close",
    attrs: { type: "button", "aria-label": "Close player" },
    html: icons.close,
  });
  const top = el(
    "div",
    { class: "player-top" },
    close,
    el(
      "div",
      { class: "player-heading" },
      el("p", { class: "player-title", text: target.title }),
      el("p", { class: "player-sub", text: target.subtitle ?? target.provider }),
    ),
  );

  const elapsed = el("span", { class: "player-time player-time--elapsed", text: "0:00" });
  const remaining = el("span", { class: "player-time player-time--remaining", text: "-0:00" });
  elapsed.setAttribute("aria-hidden", "true");
  remaining.setAttribute("aria-hidden", "true");
  const fill = el("span", { class: "player-seek-fill" });
  const buffer = el("span", { class: "player-seek-buffer" });
  const thumb = el("span", { class: "player-seek-thumb" });
  const bubble = el("span", { class: "player-bubble", text: "0:00" });
  bubble.setAttribute("aria-hidden", "true");
  const seek = el(
    "div",
    {
      class: "player-seek",
      attrs: {
        role: "slider",
        tabindex: "0",
        "aria-label": "Seek",
        "aria-valuemin": "0",
        "aria-valuemax": "0",
        "aria-valuenow": "0",
        "aria-valuetext": "0 minutes 0 seconds",
      },
    },
    el("span", { class: "player-seek-track" }, buffer, fill),
    thumb,
    bubble,
  );

  const previous = el("button", {
    class: "player-btn",
    attrs: { type: "button", "aria-label": "Previous title" },
    html: icons.previous,
  });
  const back10 = el("button", {
    class: "player-btn",
    attrs: { type: "button", "aria-label": "Back 10 seconds" },
    html: icons.back10,
  });
  const play = el("button", {
    class: "player-btn player-btn--play",
    attrs: { type: "button", "aria-label": "Play", "aria-pressed": "false" },
    html: filled(icons.play),
  });
  const forward10 = el("button", {
    class: "player-btn",
    attrs: { type: "button", "aria-label": "Forward 10 seconds" },
    html: icons.forward10,
  });
  const next = el("button", {
    class: "player-btn",
    attrs: { type: "button", "aria-label": "Next title" },
    html: icons.next,
  });
  previous.disabled = true;
  next.disabled = true;

  const mute = el("button", {
    class: "player-btn",
    attrs: { type: "button", "aria-label": "Mute", "aria-pressed": "false" },
    html: icons.volume,
  });
  const volume = el("input", {
    class: "player-volume",
    attrs: {
      type: "range",
      min: "0",
      max: "1",
      step: "0.01",
      value: "1",
      "aria-label": "Volume",
      "aria-valuetext": "100 percent",
    },
  });

  const speed = makeMenu("player-speed", "Playback speed", "1×");
  const quality = makeMenu("player-quality", "Quality", "Auto");
  const captions = el("button", {
    class: "player-btn",
    attrs: { type: "button", "aria-label": "Show captions", "aria-pressed": "false" },
    html: icons.captions,
  });
  captions.disabled = true;
  const pip = el("button", {
    class: "player-btn",
    attrs: { type: "button", "aria-label": "Picture in picture", "aria-pressed": "false" },
    html: icons.pip,
  });
  const fullscreen = el("button", {
    class: "player-btn",
    attrs: { type: "button", "aria-label": "Fullscreen", "aria-pressed": "false" },
    html: icons.fullscreen,
  });
  const autoplay = el("button", {
    class: "player-btn player-btn--text",
    attrs: { type: "button", "aria-label": "Autoplay next title", "aria-pressed": "true" },
  });
  autoplay.append(el("span", { text: "Autoplay" }));

  const transport = el(
    "div",
    { class: "player-transport" },
    previous,
    back10,
    play,
    forward10,
    next,
  );
  const settings = el(
    "div",
    { class: "player-settings" },
    mute,
    volume,
    speed.wrap,
    quality.wrap,
    captions,
    pip,
    fullscreen,
    autoplay,
  );
  const timeline = el(
    "div",
    { class: "player-timeline" },
    elapsed,
    seek,
    remaining,
  );
  const row = el("div", { class: "player-row" }, transport, settings);
  const controls = el("div", { class: "player-controls" }, timeline, row);

  const notice = el("p", { class: "player-notice", text: "Loading stream" });
  notice.hidden = false;
  notice.setAttribute("aria-hidden", "true");

  const hudIcon = el("span", { class: "player-hud-icon" });
  const hudText = el("span", { class: "player-hud-text" });
  const hud = el("div", { class: "player-hud" }, hudIcon, hudText);
  hud.setAttribute("aria-hidden", "true");
  hud.dataset.open = "false";

  const rotate = el("p", {
    class: "player-rotate",
    text: "Rotate your phone for the full frame",
  });

  const errorTitle = el("p", {
    class: "player-error-title",
    text: "This stream could not be played",
    attrs: { id: "player-error-title" },
  });
  const errorDetail = el("p", {
    class: "player-error-detail",
    text: "The source host refused the request. Try another quality or retry.",
  });
  const retry = el("button", {
    class: "player-action player-action--primary",
    attrs: { type: "button" },
    html: `${icons.retry}<span>Retry</span>`,
  });
  const errorClose = el("button", {
    class: "player-action",
    attrs: { type: "button" },
    text: "Close",
  });
  const error = el(
    "div",
    {
      class: "player-error",
      attrs: { role: "group", "aria-labelledby": "player-error-title" },
    },
    errorTitle,
    errorDetail,
    el("div", { class: "player-error-actions" }, retry, errorClose),
  );
  error.hidden = true;

  const live = el("p", {
    class: "player-live",
    attrs: { role: "status", "aria-live": "polite", "aria-atomic": "true" },
  });

  const root = el("div", {
    class: "player",
    attrs: {
      role: "dialog",
      "aria-modal": "true",
      "aria-label": `Video player: ${target.title}`,
      tabindex: "-1",
    },
  });
  root.dataset.status = "idle";
  root.dataset.chrome = "true";
  root.dataset.error = "false";
  root.dataset.open = "true";
  root.append(player, top, controls, notice, hud, rotate, error, live);

  return {
    root,
    refs: {
      root,
      video: player,
      close,
      top,
      controls,
      seek,
      fill,
      buffer,
      thumb,
      bubble,
      elapsed,
      remaining,
      previous,
      back10,
      play,
      forward10,
      next,
      mute,
      volume,
      speedTrigger: speed.trigger,
      speedPanel: speed.panel,
      qualityTrigger: quality.trigger,
      qualityPanel: quality.panel,
      captions,
      pip,
      fullscreen,
      autoplay,
      notice,
      hud,
      hudIcon,
      hudText,
      error,
      errorTitle,
      errorDetail,
      retry,
      errorClose,
      live,
      rotate,
    },
  };
}

export function openPlayer(target: PlayerTarget): void {
  teardown(false);
  const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  restoreFocus = opener && opener.isConnected ? opener : null;
  currentTarget = target;

  const built = buildOverlay(target);
  overlay = built.root;
  refs = built.refs;
  video = built.refs.video;
  const ui = built.refs;

  document.body.append(overlay);
  document.body.classList.add("player-open");

  const settings = getState().settings as {
    autoplay: boolean;
    muted: boolean;
    resume?: boolean;
    quality?: string;
  };
  autoplayEnabled = settings.autoplay;
  video.muted = settings.muted;
  ordered = [...target.media].sort((a, b) => {
    const qualityOf = (ref: MediaRef) => Number.parseInt(ref.quality ?? "0", 10) || 0;
    return qualityOf(b) - qualityOf(a);
  });
  paintAutoplay();
  paintVolume();
  paintFullscreen(false);
  paintPip(false);
  paintCaptions();

  const stored = getState().history[videoKey(target.provider, target.videoId)];
  const requested = target.startAt && target.startAt > 0 ? target.startAt : 0;
  pendingResume = requested > 0 ? requested : settings.resume && stored ? stored.position : 0;
  if (pendingResume > 0 && pendingResume < 5) pendingResume = 0;

  let initialIndex = 0;
  const wanted = settings.quality;
  if (wanted && wanted !== "auto") {
    const height = Number.parseInt(wanted, 10);
    const found = ordered.findIndex(
      (ref) => (Number.parseInt(ref.quality ?? "", 10) || 0) === height,
    );
    if (found >= 0) initialIndex = found;
  }

  const qualityItems: HTMLButtonElement[] = [];
  ordered.forEach((ref, index) => {
    qualityItems.push(
      menuItem(ui.qualityPanel, qualityLabel(ref), index === initialIndex, () => {
        const height = Number.parseInt(ref.quality ?? "", 10) || 0;
        const preferred: Quality =
          height >= 1440 ? "auto" : height >= 1080 ? "1080" : height >= 720 ? "720" : height >= 480 ? "480" : "auto";
        updateSettings({ quality: preferred });
        void attach(index, video?.currentTime ?? 0);
      }),
    );
  });
  paintQuality = () => {
    qualityItems.forEach((item, index) => item.setAttribute("aria-checked", String(index === candidate)));
    const ref = ordered[candidate];
    const label = ref ? qualityLabel(ref) : "Auto";
    ui.qualityTrigger.textContent = ref?.quality ?? "Auto";
    ui.qualityTrigger.setAttribute("aria-label", `Quality, ${label}`);
  };

  const speedItems: HTMLButtonElement[] = [];
  for (const rate of RATES) {
    speedItems.push(
      menuItem(ui.speedPanel, rate === 1 ? "Normal (1×)" : `${rate}×`, rate === 1, () => {
        if (!video) return;
        video.playbackRate = rate;
        speedItems.forEach((item, index) => {
          item.setAttribute("aria-checked", String(RATES[index] === rate));
        });
        ui.speedTrigger.textContent = `${rate}×`;
        ui.speedTrigger.setAttribute("aria-label", `Playback speed, ${rate} times`);
        announce(`Playback speed ${rate} times`);
      }),
    );
  }

  if (target.queue) {
    ui.previous.disabled = target.queue.position <= 0;
    ui.next.disabled = target.queue.position >= target.queue.length - 1;
  }

  wireMenu(ui.speedTrigger, ui.speedPanel);
  wireMenu(ui.qualityTrigger, ui.qualityPanel);

  ui.play.addEventListener("click", () => void togglePlay());
  ui.back10.addEventListener("click", () => seekBy(-10));
  ui.forward10.addEventListener("click", () => seekBy(10));
  ui.previous.addEventListener("click", () => target.queue?.onPrevious());
  ui.next.addEventListener("click", () => target.queue?.onNext());
  ui.mute.addEventListener("click", toggleMute);
  ui.captions.addEventListener("click", toggleCaptions);
  ui.pip.addEventListener("click", () => void togglePip());
  ui.fullscreen.addEventListener("click", () => void toggleFullscreen());
  ui.close.addEventListener("click", () => closePlayer());
  ui.retry.addEventListener("click", () => void attach(0));
  ui.errorClose.addEventListener("click", () => closePlayer());
  ui.autoplay.addEventListener("click", () => {
    autoplayEnabled = !autoplayEnabled;
    updateSettings({ autoplay: autoplayEnabled });
    paintAutoplay();
    announce(autoplayEnabled ? "Autoplay on" : "Autoplay off");
  });
  ui.volume.addEventListener("input", () => applyVolume(Number(ui.volume.value), false));
  ui.volume.addEventListener("change", () => {
    applyVolume(Number(ui.volume.value));
    updateSettings({ muted: ui.video.muted });
  });

  const seekFromPointer = (event: PointerEvent) => {
    const rect = ui.seek.getBoundingClientRect();
    const ratio = clamp((event.clientX - rect.left) / rect.width, 0, 1);
    seekToRatio(ratio);
  };
  let scrubbing = false;
  ui.seek.addEventListener("pointerdown", (event) => {
    scrubbing = true;
    ui.seek.setPointerCapture(event.pointerId);
    seekFromPointer(event);
    showBubble(event.clientX);
  });
  ui.seek.addEventListener("pointermove", (event) => {
    showBubble(event.clientX);
    if (scrubbing) seekFromPointer(event);
  });
  const endScrub = (event: PointerEvent) => {
    scrubbing = false;
    if (ui.seek.hasPointerCapture(event.pointerId)) ui.seek.releasePointerCapture(event.pointerId);
  };
  ui.seek.addEventListener("pointerup", endScrub);
  ui.seek.addEventListener("pointercancel", endScrub);
  ui.seek.addEventListener("pointerleave", () => {
    if (!scrubbing) ui.bubble.dataset.open = "false";
  });
  ui.seek.addEventListener("keydown", (event) => {
    const step = event.shiftKey ? 10 : 5;
    if (event.key === "ArrowRight") seekBy(step);
    else if (event.key === "ArrowLeft") seekBy(-step);
    else if (event.key === "Home") seekToRatio(0);
    else if (event.key === "End") seekToRatio(1);
    else return;
    event.preventDefault();
    event.stopPropagation();
  });

  function showBubble(clientX: number): void {
    const rect = ui.seek.getBoundingClientRect();
    const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
    const duration = Number.isFinite(video?.duration ?? 0) ? video?.duration ?? 0 : 0;
    ui.bubble.textContent = formatClock(ratio * duration);
    ui.bubble.style.setProperty("--bubble-x", `${ratio * 100}%`);
    ui.bubble.dataset.open = "true";
  }

  video.addEventListener("loadedmetadata", () => {
    if (!video) return;
    paintCaptions();
    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    const resumeAt = candidateResume || pendingResume;
    candidateResume = 0;
    pendingResume = 0;
    if (resumeAt > 0 && (!duration || resumeAt < duration - 3)) {
      video.currentTime = resumeAt;
      announce(`Resuming at ${formatClock(resumeAt)}`);
    }
    paintProgress();
  });
  video.addEventListener("durationchange", paintProgress);
  video.addEventListener("progress", paintProgress);
  video.addEventListener("timeupdate", paintProgress);
  video.addEventListener("play", () => {
    if (currentState.status === "error" || currentState.status === "ended") return;
    setPlayerState({ status: "playing", candidate });
  });
  video.addEventListener("pause", () => {
    if (currentState.status === "error" || currentState.status === "ended") return;
    if (currentState.status === "loading") return;
    setPlayerState({ status: video?.ended ? "ended" : "paused", candidate });
  });
  video.addEventListener("waiting", () => {
    if (currentState.status === "playing") setPlayerState({ status: "buffering", candidate });
  });
  video.addEventListener("playing", () => setPlayerState({ status: "playing", candidate }));
  video.addEventListener("volumechange", paintVolume);
  video.addEventListener("ratechange", () => {
    if (!video) return;
    ui.speedTrigger.textContent = `${video.playbackRate}×`;
  });
  video.textTracks.addEventListener("change", paintCaptions);
  video.textTracks.addEventListener("addtrack", paintCaptions);

  const onEnded = () => {
    setPlayerState({ status: "ended" });
    persistProgress(true);
    if (target.queue) {
      if (autoplayEnabled && target.queue.position + 1 < target.queue.length) target.queue.onNext();
    } else {
      target.onEnded?.();
    }
  };
  video.addEventListener("ended", onEnded);

  video.addEventListener("error", () => {
    if (!video?.error) return;
    failCandidate(candidate, mediaErrorMessage(video.error));
  });

  // A rendition can also fail silently: no error event, no frame, "buffering"
  // forever. Give it twelve seconds, then let the next source try.
  const element = video;
  const stallTimer = window.setTimeout(() => {
    if (element.readyState < 2 && !element.error) {
      failCandidate(candidate, "This source did not start. Trying the next one.");
    }
  }, 12000);
  const settle = (): void => window.clearTimeout(stallTimer);
  element.addEventListener("playing", settle, { once: true });
  element.addEventListener("loadeddata", settle, { once: true });
  element.addEventListener("error", settle, { once: true });
  video.addEventListener("enterpictureinpicture", () => paintPip(true));
  video.addEventListener("leavepictureinpicture", () => paintPip(false));
  video.addEventListener("webkitbeginfullscreen", () => paintFullscreen(true));
  video.addEventListener("webkitendfullscreen", () => paintFullscreen(false));
  video.addEventListener("webkitpresentationmodechanged", () => {
    const ios = video as HTMLVideoElement & { webkitPresentationMode?: string };
    paintPip(ios.webkitPresentationMode === "picture-in-picture");
  });

  let gesture: {
    pointerId: number;
    startX: number;
    startY: number;
    startVolume: number;
    startBrightness: number;
    edge: "left" | "right" | null;
    moved: boolean;
    revealed: boolean;
    pointerType: string;
  } | null = null;
  let lastTap: { at: number; zone: "left" | "right" } | null = null;

  const tapZone = (clientX: number): "left" | "right" | "center" => {
    const rect = video?.getBoundingClientRect();
    if (!rect) return "center";
    const ratio = (clientX - rect.left) / rect.width;
    if (ratio < 0.3) return "left";
    if (ratio > 0.7) return "right";
    return "center";
  };

  video.addEventListener("pointerdown", (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    const rect = video!.getBoundingClientRect();
    const ratio = (event.clientX - rect.left) / rect.width;
    const edge = ratio < 0.22 ? "left" : ratio > 0.78 ? "right" : null;
    const revealed = overlay?.dataset.chrome === "false";
    gesture = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startVolume: video!.volume,
      startBrightness: brightness,
      edge,
      moved: false,
      revealed,
      pointerType: event.pointerType,
    };
    if (event.pointerType !== "mouse") video!.setPointerCapture(event.pointerId);
    if (revealed) showChrome();
  });

  video.addEventListener("pointermove", (event) => {
    if (!gesture || event.pointerId !== gesture.pointerId) return;
    if (!gesture.edge) return;
    const dy = gesture.startY - event.clientY;
    if (Math.abs(dy) < 8) return;
    gesture.moved = true;
    const height = video?.getBoundingClientRect().height || 1;
    const delta = dy / (height * 0.7);
    if (gesture.edge === "right") applyVolume(gesture.startVolume + delta, false);
    else applyBrightness(gesture.startBrightness + delta, false);
  });

  video.addEventListener("pointerup", (event) => {
    if (!gesture || event.pointerId !== gesture.pointerId) return;
    const finished = gesture;
    gesture = null;
    if (finished.moved) {
      if (finished.edge === "right") {
        showHud(`Volume ${Math.round((video?.volume ?? 0) * 100)}%`, video?.muted ? icons.mute : icons.volume);
        announce(`Volume ${Math.round((video?.volume ?? 0) * 100)} percent`);
        updateSettings({ muted: video?.muted ?? false });
      } else if (finished.edge === "left") {
        showHud(`Brightness ${Math.round(brightness * 100)}%`);
        announce(`Brightness ${Math.round(brightness * 100)} percent`);
      }
      return;
    }
    if (finished.pointerType === "mouse") {
      if (finished.revealed) return;
      window.clearTimeout(mouseTapTimer);
      mouseTapTimer = window.setTimeout(() => void togglePlay(), 240);
      return;
    }
    const zone = tapZone(event.clientX);
    const now = performance.now();
    if (finished.revealed) {
      window.clearTimeout(tapTimer);
      lastTap = zone === "center" ? null : { at: now, zone };
      if (lastTap) {
        tapTimer = window.setTimeout(() => {
          lastTap = null;
        }, 270);
      }
      return;
    }
    if (zone !== "center" && lastTap && lastTap.zone === zone && now - lastTap.at < 320) {
      window.clearTimeout(tapTimer);
      lastTap = null;
      const delta = zone === "left" ? -10 : 10;
      seekBy(delta);
      showHud(delta > 0 ? "10 seconds forward" : "10 seconds back", delta > 0 ? icons.forward10 : icons.back10);
      return;
    }
    lastTap = zone === "center" ? null : { at: now, zone };
    window.clearTimeout(tapTimer);
    tapTimer = window.setTimeout(() => {
      lastTap = null;
      toggleChrome();
    }, 270);
  });
  video.addEventListener("pointercancel", () => {
    gesture = null;
  });
  video.addEventListener("dblclick", (event) => {
    event.preventDefault();
    window.clearTimeout(mouseTapTimer);
    void toggleFullscreen();
  });

  const onPointerDown = (event: PointerEvent) => {
    if (!activeMenu) return;
    const targetNode = event.target as Node;
    if (activeMenu.panel.contains(targetNode) || activeMenu.trigger.contains(targetNode)) return;
    closeMenu(false);
  };
  documentPointerHandler = onPointerDown;
  document.addEventListener("pointerdown", onPointerDown, true);

  const onFullscreenChange = () => {
    paintFullscreen(Boolean(document.fullscreenElement));
  };
  fullscreenHandler = onFullscreenChange;
  document.addEventListener("fullscreenchange", onFullscreenChange);

  overlay.addEventListener("pointermove", showChrome);
  overlay.addEventListener("pointerdown", showChrome);
  overlay.addEventListener("focusin", showChrome);

  keyHandler = (event) => {
    if (!overlay) return;
    if (event.key === "Escape") {
      event.preventDefault();
      if (activeMenu) closeMenu(true);
      else closePlayer();
      return;
    }
    if (event.key === "Tab") {
      trapFocus(event);
      return;
    }
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const eventTarget = event.target instanceof HTMLElement ? event.target : null;
    if (eventTarget?.closest("input, select, textarea, [contenteditable='true']")) return;
    showChrome();
    const player = video;
    if (!player) return;
    switch (event.key) {
      case " ":
        if (eventTarget?.closest("button")) return;
        event.preventDefault();
        void togglePlay();
        break;
      case "ArrowLeft":
        event.preventDefault();
        seekBy(-5);
        break;
      case "ArrowRight":
        event.preventDefault();
        seekBy(5);
        break;
      case "j":
      case "J":
        event.preventDefault();
        seekBy(-10);
        break;
      case "l":
      case "L":
        event.preventDefault();
        seekBy(10);
        break;
      case "ArrowUp":
        event.preventDefault();
        applyVolume(player.volume + 0.05);
        break;
      case "ArrowDown":
        event.preventDefault();
        applyVolume(player.volume - 0.05);
        break;
      case "m":
      case "M":
        toggleMute();
        break;
      case "f":
      case "F":
        void toggleFullscreen();
        break;
      case "p":
      case "P":
        void togglePip();
        break;
      case ",":
      case ".":
        if (!player.paused) break;
        event.preventDefault();
        player.currentTime = clamp(player.currentTime + (event.key === "." ? 1 / 30 : -1 / 30), 0, player.duration || 0);
        paintProgress();
        break;
      case "[":
        event.preventDefault();
        applyBrightness(brightness - 0.1);
        break;
      case "]":
        event.preventDefault();
        applyBrightness(brightness + 0.1);
        break;
      default:
        break;
    }
  };
  document.addEventListener("keydown", keyHandler);

  ui.play.focus({ preventScroll: true });
  void attach(initialIndex);
}

function trapFocus(event: KeyboardEvent): void {
  if (!overlay) return;
  const items = [...overlay.querySelectorAll<HTMLElement>(
    'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
  )].filter((node) => node.getClientRects().length > 0 && !node.closest("[hidden]"));
  if (items.length === 0) return;
  const first = items[0];
  const last = items[items.length - 1];
  if (!overlay.contains(document.activeElement)) {
    event.preventDefault();
    first.focus();
    return;
  }
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function teardown(restore: boolean): void {
  window.clearTimeout(hideTimer);
  window.clearTimeout(hudTimer);
  window.clearTimeout(tapTimer);
  window.clearTimeout(mouseTapTimer);
  window.clearTimeout(announceTimer);
  if (keyHandler) document.removeEventListener("keydown", keyHandler);
  keyHandler = null;
  if (fullscreenHandler) document.removeEventListener("fullscreenchange", fullscreenHandler);
  fullscreenHandler = null;
  if (documentPointerHandler) {
    document.removeEventListener("pointerdown", documentPointerHandler, true);
    documentPointerHandler = null;
  }
  if (activeMenu) closeMenu(false);
  persistProgress(true);
  if (hlsInstance) {
    hlsInstance.destroy();
    hlsInstance = null;
  }
  if (document.fullscreenElement === overlay && document.exitFullscreen) {
    void document.exitFullscreen().catch(() => undefined);
  }
  if (video && document.pictureInPictureElement === video && document.exitPictureInPicture) {
    void document.exitPictureInPicture().catch(() => undefined);
  }
  if (video) {
    video.pause();
    video.removeAttribute("src");
    video.load();
    video.style.filter = "";
  }
  clearPlayerAlert();
  if (overlay) overlay.remove();
  document.body.classList.remove("player-open");
  overlay = null;
  refs = null;
  video = null;
  currentTarget = null;
  currentState = { status: "idle" };
  generation += 1;
  brightness = 1;
  if (restore && restoreFocus?.isConnected) restoreFocus.focus({ preventScroll: true });
  restoreFocus = null;
}

/** Progressive playback goes through the resolve-and-stream route; keep the
 *  selected rendition by passing its quality label. */
function withQuality(source: string, ref: MediaRef): string {
  if (!ref.quality || !source.includes("/api/play/")) return source;
  return source.includes("?")
    ? `${source}&quality=${encodeURIComponent(ref.quality)}`
    : `${source}?quality=${encodeURIComponent(ref.quality)}`;
}

export function closePlayer(): void {
  teardown(true);
}
